import { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Skeleton, TextField, Typography } from "@mui/material";
import { UrlApi } from "../../../../../services/apirest";
import { useToast } from "../../../../../Hooks/UseToast";
import { getSemana } from "../../../common/utils/semana";
import SemanaNavigator from "../../../empleado/components/SemanaNavigator";
import DiaViandaCard from "../../../empleado/components/DiaViandaCard";
import { useDisponiblesUsuarioSemana } from "../hooks/useDisponiblesUsuarioSemana";
import { usePedidosUsuarioSemana } from "../hooks/usePedidosUsuarioSemana";
import {
  crearPedidoUsuario,
  actualizarPedidoUsuario,
  cancelarPedidoUsuario,
} from "../service/cargarPorUsuarioService";

const CargarPorUsuarioPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [busyFecha, setBusyFecha] = useState(null);
  const { showToast, Toast } = useToast();

  useEffect(() => {
    fetch(`${UrlApi}/userdata`)
      .then((res) => res.json())
      .then((data) => setUsuarios(Array.isArray(data) ? data : []))
      .catch(() => setUsuarios([]));
  }, []);

  const semana = useMemo(() => getSemana(weekOffset), [weekOffset]);

  const {
    data: disponibles,
    loading: loadingDisponibles,
    refresh: refreshDisponibles,
  } = useDisponiblesUsuarioSemana(usuario?.username, semana.desde, semana.hasta);

  const {
    data: pedidos,
    loading: loadingPedidos,
    refresh: refreshPedidos,
  } = usePedidosUsuarioSemana(usuario?.username, semana.desde, semana.hasta);

  const loading = loadingDisponibles || loadingPedidos;

  const disponiblesPorFecha = useMemo(() => {
    const map = {};
    disponibles.forEach((m) => {
      if (!map[m.fecha]) map[m.fecha] = [];
      map[m.fecha].push(m);
    });
    return map;
  }, [disponibles]);

  const pedidosPorFecha = useMemo(() => {
    const map = {};
    pedidos.forEach((p) => {
      map[p.fecha] = p;
    });
    return map;
  }, [pedidos]);

  const ejecutarAccion = async (fecha, fn) => {
    setBusyFecha(fecha);
    try {
      await fn();
      await Promise.all([refreshDisponibles(), refreshPedidos()]);
    } catch (err) {
      showToast(err.message || "Ocurrió un error inesperado.", "error");
    } finally {
      setBusyFecha(null);
    }
  };

  const handleElegir = (fecha, idMenu) =>
    ejecutarAccion(fecha, async () => {
      await crearPedidoUsuario(usuario.username, idMenu, fecha);
      showToast("Vianda cargada correctamente.", "success");
    });

  const handleCambiar = (fecha, idPedido, idMenu) =>
    ejecutarAccion(fecha, async () => {
      await actualizarPedidoUsuario(idPedido, idMenu);
      showToast("Vianda actualizada correctamente.", "success");
    });

  const handleCancelar = (fecha, idPedido) =>
    ejecutarAccion(fecha, async () => {
      await cancelarPedidoUsuario(idPedido);
      showToast("Pedido cancelado.", "info");
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="info">
        Elegí un usuario de la tabla de usuarios para cargarle, cambiarle o cancelarle su
        vianda de cualquier día, aunque la semana ya esté cerrada para autogestión.
      </Alert>

      <Autocomplete
        size="small"
        sx={{ maxWidth: 420 }}
        options={usuarios}
        value={usuario}
        onChange={(_, val) => setUsuario(val)}
        isOptionEqualToValue={(option, value) => option.username === value?.username}
        getOptionLabel={(u) => `${u.username} — ${u.nombrepila || ""} ${u.apellido || ""}`.trim()}
        renderInput={(params) => <TextField {...params} label="Usuario" placeholder="Buscar por nombre o username..." />}
      />

      {!usuario && (
        <Typography variant="body2" color="text.secondary">
          Elegí un usuario para ver y cargarle su vianda semanal.
        </Typography>
      )}

      {usuario && (
        <>
          <SemanaNavigator
            desde={semana.desde}
            hasta={semana.hasta}
            esSemanaActual={semana.esSemanaActual}
            onPrev={() => setWeekOffset((o) => o - 1)}
            onNext={() => setWeekOffset((o) => o + 1)}
            onHoy={() => setWeekOffset(0)}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {loading &&
              semana.dias.map((d) => (
                <Skeleton key={d.fecha} variant="rounded" height={220} sx={{ borderRadius: 4 }} />
              ))}

            {!loading &&
              semana.dias.map((dia) => (
                <DiaViandaCard
                  key={dia.fecha}
                  dia={dia}
                  menus={disponiblesPorFecha[dia.fecha] || []}
                  pedido={pedidosPorFecha[dia.fecha]}
                  onElegir={handleElegir}
                  onCambiar={(idPedido, idMenu) => handleCambiar(dia.fecha, idPedido, idMenu)}
                  onCancelar={(idPedido) => handleCancelar(dia.fecha, idPedido)}
                  busy={busyFecha === dia.fecha}
                />
              ))}
          </Box>
        </>
      )}

      {Toast}
    </Box>
  );
};

export default CargarPorUsuarioPage;
